import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Sidebar } from "@/components/Sidebar";
import AIChat from "@/components/AIChat";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Wrench, Plus, Calendar as CalIcon, Clock, User, Phone, Car, MessageSquare,
  Brain, AlertTriangle, CheckCircle, Bot, ChevronRight, Sparkles
} from "lucide-react";

export default function ServicePage() {
  const [appointments, setAppointments] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showAddAppt, setShowAddAppt] = useState(false);
  const [newAppt, setNewAppt] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    service_type: "maintenance", vehicle_info: "",
    scheduled_date: new Date().toISOString().split('T')[0], scheduled_time: "09:00"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [apptsRes, workersRes] = await Promise.all([
        axios.get(`${API}/appointments`),
        axios.get(`${API}/digital-workers/service`)
      ]);
      setAppointments(apptsRes.data);
      setWorkers(workersRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAppt = async () => {
    try {
      await axios.post(`${API}/appointments`, newAppt);
      setShowAddAppt(false);
      setNewAppt({
        customer_name: "", customer_email: "", customer_phone: "",
        service_type: "maintenance", vehicle_info: "",
        scheduled_date: new Date().toISOString().split('T')[0], scheduled_time: "09:00"
      });
      fetchData();
    } catch (error) {
      console.error("Error adding appointment:", error);
    }
  };

  const getRiskBadge = (risk) => {
    const colors = {
      high: "bg-red-500/10 text-red-500 border-red-500/20",
      medium: "bg-amber-500/10 text-amber-500 border-amber-500/20",
      low: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20"
    };
    return colors[risk] || colors.low;
  };

  const getStatusIcon = (status) => {
    const icons = {
      scheduled: <Clock className="w-4 h-4 text-blue-500" />,
      confirmed: <CheckCircle className="w-4 h-4 text-emerald-500" />,
      in_progress: <Wrench className="w-4 h-4 text-amber-500" />,
      completed: <CheckCircle className="w-4 h-4 text-emerald-500" />,
      cancelled: <AlertTriangle className="w-4 h-4 text-red-500" />
    };
    return icons[status] || icons.scheduled;
  };

  const todayStr = new Date().toISOString().split('T')[0];
  const todayAppointments = appointments.filter(a => a.scheduled_date === todayStr);
  const highRiskAppointments = appointments.filter(a => a.no_show_risk === "high");

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-content">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-3" data-testid="service-title">
              <Wrench className="w-8 h-8 text-emerald-500" />
              Service
            </h1>
            <p className="text-muted-foreground">AI-powered appointment scheduling and service management</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowChat(true)} className="gap-2">
              <Brain className="w-4 h-4" /> AI Assistant
            </Button>
            <Dialog open={showAddAppt} onOpenChange={setShowAddAppt}>
              <DialogTrigger asChild>
                <Button className="btn-ai gap-2" data-testid="add-appt-btn">
                  <Plus className="w-4 h-4" /> Schedule Appointment
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Schedule Service Appointment</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <Input placeholder="Customer Name" value={newAppt.customer_name} onChange={(e) => setNewAppt({...newAppt, customer_name: e.target.value})} />
                  <Input placeholder="Email" type="email" value={newAppt.customer_email} onChange={(e) => setNewAppt({...newAppt, customer_email: e.target.value})} />
                  <Input placeholder="Phone" value={newAppt.customer_phone} onChange={(e) => setNewAppt({...newAppt, customer_phone: e.target.value})} />
                  <Input placeholder="Vehicle (Year Make Model)" value={newAppt.vehicle_info} onChange={(e) => setNewAppt({...newAppt, vehicle_info: e.target.value})} />
                  <Select value={newAppt.service_type} onValueChange={(v) => setNewAppt({...newAppt, service_type: v})}>
                    <SelectTrigger><SelectValue placeholder="Service Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="repair">Repair</SelectItem>
                      <SelectItem value="inspection">Inspection</SelectItem>
                      <SelectItem value="recall">Recall Service</SelectItem>
                      <SelectItem value="warranty">Warranty Work</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="grid grid-cols-2 gap-4">
                    <Input type="date" value={newAppt.scheduled_date} onChange={(e) => setNewAppt({...newAppt, scheduled_date: e.target.value})} />
                    <Input type="time" value={newAppt.scheduled_time} onChange={(e) => setNewAppt({...newAppt, scheduled_time: e.target.value})} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddAppt(false)}>Cancel</Button>
                  <Button className="btn-ai" onClick={handleAddAppt}>Schedule</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Tabs defaultValue="appointments" className="space-y-6">
          <TabsList>
            <TabsTrigger value="appointments" className="gap-2"><CalIcon className="w-4 h-4" /> Appointments</TabsTrigger>
            <TabsTrigger value="workers" className="gap-2"><Bot className="w-4 h-4" /> Digital Workers</TabsTrigger>
          </TabsList>

          {/* Appointments Tab */}
          <TabsContent value="appointments" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Today's Appointments", value: todayAppointments.length, icon: CalIcon, color: "text-emerald-500" },
                { label: "High No-Show Risk", value: highRiskAppointments.length, icon: AlertTriangle, color: "text-red-500" },
                { label: "In Progress", value: appointments.filter(a => a.status === "in_progress").length, icon: Wrench, color: "text-amber-500" },
                { label: "Completed Today", value: appointments.filter(a => a.status === "completed" && a.scheduled_date === todayStr).length, icon: CheckCircle, color: "text-blue-500" },
              ].map((stat, idx) => (
                <Card key={idx}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg ${stat.color.replace('text-', 'bg-')}/10 flex items-center justify-center`}>
                      <stat.icon className={`w-5 h-5 ${stat.color}`} />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{stat.value}</div>
                      <div className="text-xs text-muted-foreground">{stat.label}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Calendar and Appointments */}
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Calendar */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="text-lg">Calendar</CardTitle>
                </CardHeader>
                <CardContent>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    className="rounded-md"
                  />
                </CardContent>
              </Card>

              {/* Appointments List */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Appointments</CardTitle>
                  <CardDescription>Showing all scheduled appointments</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 max-h-[500px] overflow-y-auto">
                  {loading ? (
                    <div className="text-center py-8 text-muted-foreground">Loading...</div>
                  ) : appointments.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">No appointments scheduled</div>
                  ) : (
                    appointments.map((appt) => (
                      <div key={appt.id} className="p-4 rounded-lg border border-border hover:border-emerald-500/30 transition-all" data-testid={`appt-${appt.id}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            {getStatusIcon(appt.status)}
                            <div>
                              <div className="font-semibold text-foreground">{appt.customer_name}</div>
                              <div className="text-sm text-muted-foreground">{appt.vehicle_info || "Vehicle not specified"}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={getRiskBadge(appt.no_show_risk)}>
                              {appt.no_show_risk} risk
                            </Badge>
                            <Badge variant="outline">{appt.service_type}</Badge>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <CalIcon className="w-4 h-4" /> {appt.scheduled_date}
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" /> {appt.scheduled_time}
                          </div>
                          <div className="flex items-center gap-1">
                            <Phone className="w-4 h-4" /> {appt.customer_phone}
                          </div>
                        </div>

                        {appt.ai_recommendation && (
                          <div className="mt-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 flex items-start gap-2">
                            <Sparkles className="w-4 h-4 text-amber-500 mt-0.5" />
                            <span className="text-sm text-amber-400">{appt.ai_recommendation}</span>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Digital Workers Tab */}
          <TabsContent value="workers" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {workers.map((worker) => (
                <Card key={worker.id} className="hover:border-emerald-500/30 transition-all group" data-testid={`worker-${worker.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Bot className="w-6 h-6 text-emerald-500" />
                      </div>
                      <Badge className="bg-emerald-500/10 text-emerald-500">{worker.status}</Badge>
                    </div>
                    <CardTitle className="text-lg mt-3">{worker.name}</CardTitle>
                    <CardDescription className="text-emerald-400">{worker.subtitle}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">{worker.description}</p>
                    
                    <div className="space-y-2">
                      {worker.features?.slice(0, 4).map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <ChevronRight className="w-3 h-3 text-emerald-500" />
                          {feature}
                        </div>
                      ))}
                    </div>

                    {worker.metrics && (
                      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-border">
                        {Object.entries(worker.metrics).slice(0, 3).map(([key, value]) => (
                          <div key={key} className="text-center">
                            <div className="text-sm font-bold text-foreground">{value}</div>
                            <div className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <AIChat isOpen={showChat} onClose={() => setShowChat(false)} vertical="service" />
      
      {!showChat && (
        <button onClick={() => setShowChat(true)} className="fixed bottom-6 right-6 w-14 h-14 rounded-full btn-ai flex items-center justify-center shadow-lg hover:scale-110 transition-transform" style={{ zIndex: 9998 }} data-testid="chat-fab">
          <MessageSquare className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
